/*
 * Copyright 2023 The Android Open Source Project
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
package org.multipaz.securearea.software

import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.currentCoroutineContext
import org.multipaz.crypto.Algorithm
import org.multipaz.crypto.Crypto
import org.multipaz.crypto.EcPrivateKey
import org.multipaz.crypto.EcPublicKey
import org.multipaz.crypto.MlDsaPrivateKey
import org.multipaz.crypto.MlKemPrivateKey
import org.multipaz.crypto.PrivateKey
import org.multipaz.crypto.PublicKey
import org.multipaz.crypto.RsaPrivateKey
import org.multipaz.crypto.SecretKey
import org.multipaz.crypto.SecureByteString
import org.multipaz.crypto.Signature
import org.multipaz.crypto.secureZero
import org.multipaz.prompt.requestPassphrase
import org.multipaz.securearea.KeyAttestation
import org.multipaz.securearea.KeyLockedException
import org.multipaz.securearea.KeyUnlockData
import org.multipaz.securearea.PassphraseConstraints
import org.multipaz.securearea.SecureArea
import org.multipaz.storage.Storage
import org.multipaz.storage.StorageTable
import org.multipaz.storage.StorageTableSpec
import kotlinx.io.bytestring.ByteString
import org.multipaz.cbor.Cbor
import org.multipaz.cbor.annotation.CborSerializable
import org.multipaz.crypto.Hkdf
import org.multipaz.prompt.PassphraseEvaluation
import org.multipaz.prompt.PromptDismissedException
import org.multipaz.prompt.PromptModel
import org.multipaz.prompt.PromptModelNotAvailableException
import org.multipaz.securearea.KeyUnlockDataProvider
import org.multipaz.prompt.Reason
import org.multipaz.securearea.KeyInfo
import kotlin.random.Random

/**
 * An implementation of [SecureArea] that creates and uses software keys.
 *
 * This implementation supports all the curves and algorithms defined by [SecureArea]
 * and also supports passphrase-protected keys. Key material is stored using the
 * [Storage] abstraction and passphrase-protected keys are encrypted using
 * [AES-GCM](https://en.wikipedia.org/wiki/Galois/Counter_Mode)
 * with 256-bit keys with the key derived from the passphrase using
 * [HKDF](https://en.wikipedia.org/wiki/HKDF).
 *
 * On JVM and Android this is using [Crypto] and the algorithms and curves it implements.
 *
 * Use [SoftwareSecureArea.create] to create an instance of SoftwareSecureArea.
 */
class SoftwareSecureArea private constructor(
    private val storageTable: StorageTable,
    private val random: Random = Crypto.secureRandom
) : SecureArea {
    override val identifier get() = IDENTIFIER

    override val displayName get() = "Software Secure Area"

    private val supportedAlgorithms_: List<Algorithm> by lazy {
        Algorithm.entries.filter {
            it.fullySpecified && (
                (it.curve != null && Crypto.supportedCurves.contains(it.curve)) ||
                (it.keySizeBits != null && it.isSigning) ||
                (it in Crypto.supportedMlDsaAlgorithms) ||
                (it in Crypto.supportedMlKemAlgorithms)
            )
        }
    }

    override val supportedAlgorithms: List<Algorithm>
        get() = supportedAlgorithms_

    override suspend fun createKey(
        alias: String?,
        createKeySettings: org.multipaz.securearea.CreateKeySettings
    ): SoftwareKeyInfo {
        if (alias != null) {
            // If the key with the given alias exists, it is silently overwritten.
            storageTable.delete(alias)
        }

        val settings = if (createKeySettings is SoftwareCreateKeySettings) {
            createKeySettings
        } else {
            // If user passed in a generic SecureArea.CreateKeySettings, honor them.
            val builder = SoftwareCreateKeySettings.Builder()
                .setAlgorithm(createKeySettings.algorithm)
                .setUserAuthenticationRequired(
                    required = createKeySettings.userAuthenticationRequired,
                    types = setOf(
                        SoftwareUserAuthType.PASSCODE,
                        SoftwareUserAuthType.BIOMETRIC,
                    )
                )
            if (createKeySettings.validFrom != null && createKeySettings.validUntil != null) {
                builder.setValidityPeriod(
                    validFrom = createKeySettings.validFrom,
                    validUntil = createKeySettings.validUntil
                )
            }
            builder.build()
        }
        try {
            val privateKey = settings.privateKey ?: if (settings.algorithm.curve != null) {
                Crypto.createEcPrivateKey(settings.algorithm.curve!!)
            } else if (settings.algorithm in listOf(Algorithm.ML_DSA_44, Algorithm.ML_DSA_65, Algorithm.ML_DSA_87)) {
                Crypto.createMlDsaPrivateKey(settings.algorithm)
            } else if (settings.algorithm in listOf(Algorithm.ML_KEM_512, Algorithm.ML_KEM_768, Algorithm.ML_KEM_1024)) {
                Crypto.createMlKemPrivateKey(settings.algorithm)
            } else {
                Crypto.createRsaPrivateKey(settings.algorithm.keySizeBits ?: 2048)
            }
            val encodedPublicKey = Cbor.encode(privateKey.publicKey.toCoseKey().toDataItem())
            val keyMetadata = if (settings.passphraseRequired) {
                val secretKey = derivePrivateKeyEncryptionKey(encodedPublicKey, settings.passphrase!!)
                val cleartextPrivateKey = Cbor.encode(privateKey.toCoseKey().toDataItem())
                val iv = random.nextBytes(12)
                val encryptedPrivateKey = try {
                    Crypto.encrypt(
                        Algorithm.A256GCM,
                        secretKey,
                        iv,
                        cleartextPrivateKey
                    )
                } finally {
                    secretKey.close()
                    cleartextPrivateKey.secureZero()
                }
                privateKey.close()
                KeyMetadata(
                    algorithm = settings.algorithm,
                    passphraseRequired = true,
                    privateKey = null,
                    encryptedPrivateKey = ByteString(encryptedPrivateKey),
                    encryptedPrivateKeyIv = ByteString(iv),
                    encodedPublicKey = ByteString(encodedPublicKey),
                    passphraseConstraints = settings.passphraseConstraints,
                    userAuthenticationRequired = if (settings.userAuthenticationRequired) true else null,
                    userAuthenticationTypes = if (settings.userAuthenticationRequired) settings.userAuthenticationTypes else null
                )
            } else {
                KeyMetadata(
                    algorithm = settings.algorithm,
                    passphraseRequired = false,
                    privateKey = privateKey,
                    encryptedPrivateKey = null,
                    encryptedPrivateKeyIv = null,
                    encodedPublicKey = ByteString(encodedPublicKey),
                    passphraseConstraints = null,
                    userAuthenticationRequired = if (settings.userAuthenticationRequired) true else null,
                    userAuthenticationTypes = if (settings.userAuthenticationRequired) settings.userAuthenticationTypes else null
                )
            }
            val newAlias = storageTable.insert(
                key = alias,
                data = ByteString(keyMetadata.toCbor())
            )
            return getKeyInfo(newAlias)
        } catch (e: Exception) {
            if (e is CancellationException) throw e
            throw IllegalStateException("Unexpected exception", e)
        }
    }

    private suspend fun derivePrivateKeyEncryptionKey(
        encodedPublicKey: ByteArray,
        passphrase: String
    ): SecretKey {
        val info = "ICPrivateKeyEncryption1".encodeToByteArray()
        val ikm = passphrase.encodeToByteArray()
        return try {
            Hkdf.deriveKey(
                Algorithm.HMAC_SHA256,
                SecretKey(ikm),
                encodedPublicKey,
                info,
                32
            )
        } finally {
            ikm.secureZero()
        }
    }

    override suspend fun deleteKey(alias: String) {
        storageTable.delete(alias)
    }

    private data class KeyData(
        val algorithm: Algorithm,
        val privateKey: PrivateKey,
    )

    private suspend fun loadKey(
        alias: String,
        keyUnlockData: KeyUnlockData?
    ): KeyData {
        val data = storageTable.get(alias)
            ?: throw IllegalArgumentException("No key with given alias")
        val keyMetadata = KeyMetadata.fromCbor(data.toByteArray())

        var passphrase: String? = null
        var userAuthenticated = false
        if (keyUnlockData != null) {
            val unlockData = keyUnlockData as SoftwareKeyUnlockData
            passphrase = unlockData.passphrase
            userAuthenticated = unlockData.userAuthenticated
        }

        if (keyMetadata.userAuthenticationRequired == true && !userAuthenticated) {
            throw KeyLockedException("User authentication required")
        }

        val privateKey = if (keyMetadata.passphraseRequired) {
            if (passphrase == null) {
                throw KeyLockedException("No passphrase provided")
            }
            val encodedPublicKey = keyMetadata.encodedPublicKey.toByteArray()
            val encryptedPrivateKey = keyMetadata.encryptedPrivateKey!!.toByteArray()
            val iv = keyMetadata.encryptedPrivateKeyIv!!.toByteArray()
            val secretKey = derivePrivateKeyEncryptionKey(encodedPublicKey, passphrase)
            val encodedPrivateKey = try {
                Crypto.decrypt(Algorithm.A128GCM, secretKey, iv, encryptedPrivateKey)
            } catch (e: Exception) {
                if (e is CancellationException) throw e
                throw KeyLockedException("Error decrypting private key - wrong passphrase?", e)
            } finally {
                secretKey.close()
            }
            val parsedKey = try {
                PrivateKey.fromDataItem(Cbor.decode(encodedPrivateKey))
            } finally {
                encodedPrivateKey.secureZero()
            }
            parsedKey
        } else {
            keyMetadata.privateKey!!
        }
        return KeyData(keyMetadata.algorithm, privateKey)
    }

    /**
     * Gets the underlying private key.
     *
     * @param alias the alias for the key.
     * @param keyUnlockData unlock data, or `null`.
     * @return a [PrivateKey].
     * @throws IllegalArgumentException if no key with the given alias exists.
     * @throws KeyLockedException if the key needs unlocking.
     */
    @Throws(
        IllegalArgumentException::class,
        KeyLockedException::class,
        CancellationException::class
    )
    suspend fun getPrivateKey(
        alias: String,
        keyUnlockData: KeyUnlockData?
    ): PrivateKey = loadKey(alias, keyUnlockData).privateKey

    private suspend fun<T> interactionHelper(
        alias: String,
        unlockReason: Reason,
        op: suspend (unlockData: KeyUnlockData?) -> T,
    ): T =
        try {
            op.invoke(null)
        } catch (_: KeyLockedException) {
            val unlockDataProvider = currentCoroutineContext()[KeyUnlockDataProvider.Key]
                ?: DefaultKeyUnlockDataProvider
            // DefaultKeyUnlockDataProvider.getKeyUnlockData checks passphrase/PIN before
            // returning it here, so retry loop is internal to the provider. We expect
            // other providers to do the same, as we do not want to subject non-interactive
            // providers to handle repeated calls when unlock data fails to unlock the key.
            val unlockData = unlockDataProvider.getKeyUnlockData(
                secureArea = this,
                alias = alias,
                algorithm = getKeyInfo(alias).algorithm,
                unlockReason = unlockReason
            )
            op.invoke(unlockData)
        }

    override suspend fun sign(
        alias: String,
        dataToSign: ByteArray,
        unlockReason: Reason
    ): Signature {
        return interactionHelper(
            alias,
            unlockReason,
            op = { unlockData -> signNonInteractive(alias, dataToSign, unlockData) }
        )
    }

    private suspend fun signNonInteractive(
        alias: String,
        dataToSign: ByteArray,
        keyUnlockData: KeyUnlockData?
    ): Signature {
        val keyData = loadKey(alias, keyUnlockData)
        return keyData.privateKey.use { privateKey ->
            require(keyData.algorithm.isSigning) { "Key algorithm is not for Signing" }
            when (privateKey) {
                is EcPrivateKey -> Crypto.sign(privateKey, keyData.algorithm, dataToSign)
                is RsaPrivateKey -> Crypto.sign(privateKey, keyData.algorithm, dataToSign)
                is MlDsaPrivateKey -> Crypto.sign(privateKey, keyData.algorithm, dataToSign)
                is MlKemPrivateKey -> throw IllegalArgumentException("Cannot sign with ML-KEM key")
            }
        }
    }

    override suspend fun keyAgreement(
        alias: String,
        otherKey: EcPublicKey,
        unlockReason: Reason
    ): SecureByteString {
        return interactionHelper(
            alias,
            unlockReason,
            op = { unlockData -> keyAgreementNonInteractive(alias, otherKey, unlockData) }
        )
    }

    private suspend fun keyAgreementNonInteractive(
        alias: String,
        otherKey: EcPublicKey,
        keyUnlockData: KeyUnlockData?
    ): SecureByteString {
        val keyData = loadKey(alias, keyUnlockData)
        return keyData.privateKey.use { privateKey ->
            require(keyData.algorithm.isKeyAgreement) { "Key algorithm is not for Key Agreement" }
            val ecPrivateKey = privateKey as? EcPrivateKey
                ?: throw IllegalArgumentException("Key is not an EC key")
            Crypto.keyAgreement(ecPrivateKey, otherKey)
        }
    }

    override suspend fun kemDecapsulate(
        alias: String,
        ciphertext: ByteArray,
        unlockReason: Reason
    ): SecureByteString {
        return interactionHelper(
            alias,
            unlockReason,
            op = { unlockData -> kemDecapsulateNonInteractive(alias, ciphertext, unlockData) }
        )
    }

    private suspend fun kemDecapsulateNonInteractive(
        alias: String,
        ciphertext: ByteArray,
        keyUnlockData: KeyUnlockData?
    ): SecureByteString {
        val keyData = loadKey(alias, keyUnlockData)
        return keyData.privateKey.use { privateKey ->
            require(keyData.algorithm.isKeyEncapsulation) { "Key algorithm is not for Key Encapsulation" }
            val mlKemPrivateKey = privateKey as? MlKemPrivateKey
                ?: throw IllegalArgumentException("Key is not an ML-KEM key")
            Crypto.kemDecapsulate(mlKemPrivateKey, ciphertext)
        }
    }

    override suspend fun getKeyInfo(alias: String): SoftwareKeyInfo {
        val data = storageTable.get(alias)
            ?: throw IllegalArgumentException("No key with the given alias '$alias'")
        val keyMetadata = KeyMetadata.fromCbor(data.toByteArray())
        val publicKey = PublicKey.fromDataItem(Cbor.decode(keyMetadata.encodedPublicKey.toByteArray()))
        return SoftwareKeyInfo(
            alias,
            publicKey,
            KeyAttestation(publicKey, null),
            keyMetadata.algorithm,
            keyMetadata.passphraseRequired,
            keyMetadata.passphraseConstraints,
            keyMetadata.userAuthenticationRequired ?: false,
            keyMetadata.userAuthenticationTypes ?: emptySet()
        )
    }

    override suspend fun getKeyInvalidated(alias: String): Boolean {
        // Software keys are never invalidated.
        return false
    }

    override suspend fun unlockKey(
        alias: String,
        unlockReason: Reason
    ): List<KeyUnlockData> {
        val keyInfo = getKeyInfo(alias)
        if (!keyInfo.isPassphraseProtected && !keyInfo.isUserAuthenticationRequired) {
            return emptyList()
        }
        val unlockDataProvider = currentCoroutineContext()[KeyUnlockDataProvider.Key]
            ?: DefaultKeyUnlockDataProvider
        val unlockData = unlockDataProvider.getKeyUnlockData(
            secureArea = this,
            alias = alias,
            algorithm = keyInfo.algorithm,
            unlockReason = unlockReason
        )
        return listOf(unlockData)
    }

    object DefaultKeyUnlockDataProvider: KeyUnlockDataProvider {
        override suspend fun getKeyUnlockData(
            secureArea: SecureArea,
            alias: String,
            algorithm: Algorithm,
            unlockReason: Reason
        ): KeyUnlockData {
            secureArea as SoftwareSecureArea
            val keyInfo = secureArea.getKeyInfo(alias)

            var passphrase: String? = null
            if (keyInfo.isPassphraseProtected) {
                val constraints = keyInfo.passphraseConstraints ?: PassphraseConstraints.NONE
                val promptModel = try {
                    PromptModel.get()
                } catch (_: PromptModelNotAvailableException) {
                    throw KeyLockedException("Key is locked and PromptModel is not available to unlock interactively")
                }
                passphrase = try {
                    promptModel.requestPassphrase(
                        reason = unlockReason,
                        passphraseConstraints = constraints,
                        passphraseEvaluator = { enteredPassphrase: String ->
                            try {
                                secureArea.loadKey(
                                    alias,
                                    SoftwareKeyUnlockData(
                                        secureArea,
                                        alias,
                                        passphrase = enteredPassphrase,
                                        userAuthenticated = true
                                    )
                                )
                                PassphraseEvaluation.OK
                            } catch (e: Exception) {
                                if (e is CancellationException) throw e
                                PassphraseEvaluation.TryAgain
                            }
                        }
                    )
                } catch (_: PromptDismissedException) {
                    throw KeyLockedException("User canceled passphrase prompt")
                }
            }

            var userAuthenticated = false
            if (keyInfo.isUserAuthenticationRequired) {
                if (!softwareSecureAreaPerformUserAuth(alias, keyInfo.userAuthenticationTypes, unlockReason)) {
                    throw KeyLockedException("User canceled authentication")
                }
                userAuthenticated = true
            }

            return SoftwareKeyUnlockData(
                secureArea = secureArea,
                alias = alias,
                passphrase = passphrase,
                userAuthenticated = userAuthenticated
            )
        }
    }

    @CborSerializable(schemaHash = "ZQhm_YwMdgsTBNuJjP_Zv5HQL2dKaVLwMRH96nxpf8Q")
    internal data class KeyMetadata(
        val algorithm: Algorithm,
        val passphraseRequired: Boolean,
        val privateKey: PrivateKey?,
        val encryptedPrivateKey: ByteString?,
        val encryptedPrivateKeyIv: ByteString?,
        val encodedPublicKey: ByteString,  // store as encoded CoseKey
        val passphraseConstraints: PassphraseConstraints?,
        val userAuthenticationRequired: Boolean? = null,
        val userAuthenticationTypes: Set<SoftwareUserAuthType>? = null
    ) {
        companion object
    }

    companion object {
        private const val TAG = "SoftwareSecureArea"

        const val IDENTIFIER = "SoftwareSecureArea"

        /**
         * Creates an instance of SoftwareSecureArea using default [Crypto.secureRandom].
         *
         * @param storage the storage engine to use for storing key material.
         */
        suspend fun create(
            storage: Storage
        ): SoftwareSecureArea = create(storage, Crypto.secureRandom)

        /**
         * Creates an instance of SoftwareSecureArea.
         *
         * @param storage the storage engine to use for storing key material.
         * @param random the random provider to use.
         */
        suspend fun create(
            storage: Storage,
            random: Random
        ): SoftwareSecureArea {
            return SoftwareSecureArea(storage.getTable(tableSpec), random)
        }

        private val tableSpec = StorageTableSpec(
            name = "SoftwareSecureArea",
            supportPartitions = false,
            supportExpiration = false
        )
    }
}
